import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertCircle, Save, Database, Flame, Copy } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import api, { apiError } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState } from "@/components/ui/page";
import { ClientForm } from "@/components/ClientForm";
import { Button } from "@/components/ui/button";

export default function Settings() {
  const { selected, isAll, currentClient } = useClient();
  const { isSuperAdmin } = useAuth();
  const qc = useQueryClient();
  const single = !isAll && selected;

  const { data, isLoading } = useQuery({
    queryKey: ["client", selected],
    queryFn: async () => (await api.get(`/clients/${selected}`)).data,
    enabled: !!single,
  });
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm(data); }, [data]);

  const mut = useMutation({
    mutationFn: async () => (await api.put(`/clients/${selected}`, form)).data,
    onSuccess: () => { toast.success("Settings saved"); qc.invalidateQueries({ queryKey: ["clients"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const migrate = useMutation({
    mutationFn: async () => (await api.post(`/admin/migrate-deals-to-won`)).data,
    onSuccess: (d) => { toast.success(`Migrated ${d.migrated} deal(s) to Won`); qc.invalidateQueries(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (isAll) return <div><PageHeader title="Settings" /><EmptyState icon={AlertCircle} title="Select a client" subtitle="Choose a specific client to view its settings." /></div>;
  if (isLoading || !form) return <Spinner />;

  return (
    <div className="max-w-3xl">
      <PageHeader title="Client Settings" subtitle={currentClient?.business_name}>
        {isSuperAdmin && (
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="outline" data-testid="migrate-deals-btn"><Database className="h-4 w-4 mr-1.5" /> Migrate Deals → Won</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Migrate all DEAL contacts to WON?</AlertDialogTitle><AlertDialogDescription>Moves every current "Deal" contact (across all clients) into the "Won" stage so signed revenue counts under Won. Safe to run once; running again does nothing.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => migrate.mutate()} data-testid="confirm-migrate-btn">Migrate</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {isSuperAdmin && <Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="save-settings-btn"><Save className="h-4 w-4 mr-1.5" /> Save Changes</Button>}
      </PageHeader>
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <ClientForm value={form} onChange={setForm} disabled={!isSuperAdmin} />
        {!isSuperAdmin && <p className="mt-4 text-sm text-slate-400">You have read-only access to these settings. Only a VSM super admin can edit client configuration.</p>}
      </div>
      {isSuperAdmin && form.brevo_configured && <BrevoWebhookCard clientId={selected} />}
      <p className="text-xs text-slate-400 mt-3">Average Job Size is used to estimate signed deal value when an actual deal amount hasn't been entered.</p>
    </div>
  );
}

function BrevoWebhookCard({ clientId }) {
  const { data } = useQuery({
    queryKey: ["brevo-webhook-url", clientId],
    queryFn: async () => (await api.get(`/clients/${clientId}/brevo/webhook-url`)).data,
    enabled: !!clientId,
  });
  const url = data?.url || "";
  const copy = () => { navigator.clipboard.writeText(url); toast.success("Webhook URL copied"); };
  return (
    <div className="bg-white border border-orange-200 rounded-xl p-6 mt-4" data-testid="brevo-webhook-card">
      <div className="flex items-center gap-2 font-semibold text-slate-900 mb-1"><Flame className="h-4 w-4 text-orange-500" /> Warm-Lead Webhook (Brevo)</div>
      <p className="text-sm text-slate-500 mb-3">In Brevo → <b>Settings → Webhooks</b> (Marketing), create a webhook for the <b>Opened</b> and <b>Clicked</b> events and paste this URL. Any contact who opens or clicks a Brevo email — including Automations — is flagged 🔥 Warm on the Contacts list.</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 break-all" data-testid="brevo-webhook-url">{url || "Loading…"}</code>
        <Button variant="outline" size="sm" onClick={copy} disabled={!url} data-testid="copy-webhook-url"><Copy className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
